import { IUser } from '@interfaces/IUser';
import { Auth } from 'aws-amplify';
import jwtDecode from 'jwt-decode';

import BasePaginationQuery from '../interfaces/base-pagination-query';
import IPagination from '../interfaces/IPagination';
import BaseService, { RequestInterface } from './Base';
import { AwsConfig } from './Constants';


export default class UserService extends BaseService {

    constructor () {
        super(AwsConfig.API_CONFIG, 'user');
    }

    async signIn (username: string, password: string) {
        try {
            const res = await Auth.signIn(username, password);
            return res;
        } catch (error) {
            const res = this._Errors.get(error);
            throw res
        }
    }
    async signOut (): Promise<void> {
        try {
            await Auth.signOut();
        } catch (error) {
            const res = this._Errors.get(error);
            throw res
        }
    }
    async recoverPassword (username: string, ) {
        try {
            return await Auth.forgotPassword(username);
        } catch (error) {
            const res = this._Errors.get(error);
            throw res
        }
    }
    async redefinePassword (username: string, code: string, password: string) {
        try {
            return await Auth.forgotPasswordSubmit(username, code, password);
        } catch (error) {
            const res = this._Errors.get(error);
            throw res
        }
    }
    async changePassword (username: string, oldPassword: string, newPassword: string) {
        try {
            return await Auth.changePassword(username, oldPassword, newPassword);
        } catch (error) {
            const res = this._Errors.get(error);
            throw res
        }
    }
    async getCredentials () {
        if (!Auth) return null;
        return await Auth.currentCredentials();
    }
    async getToken () {
        const currentSession = await Auth.currentSession();
        return currentSession.idToken.jwtToken;
    }
    async getDecodedToken () {
        return await jwtDecode(await this.getToken());
    }
    async isAuthenticated (): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            Auth.currentSession().then(async session => {
                const isValid: boolean = new Date(session.idToken.payload.exp * 1000) > new Date();
                resolve(isValid);
            })
                .catch(reject);
        });
    }

    async me (): Promise<IUser> {
        const currentUserInfo = await Auth.currentUserInfo();
        const token: any = await this.getDecodedToken();
        const roles: IUser = await this.myRoles();

        return {
            id: token.sub,
            username: token['cognito:username'],
            name: token.name,
            phone: currentUserInfo.attributes.phone_number,
            email: token.email,
            picture: currentUserInfo.attributes.picture || 'assets/images/avatar.png',
            disabled: false,
            units: currentUserInfo.units,
            roles: roles.roles,
            permission: roles.permissions
        } as IUser;

    }

    //FIXME: unit is a bare string. this is a problem
    async myUnits (): Promise<IUser[]> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(['units'])
        };

        const res = await super.get<IUser[]>('me', params);
        return res;
    }

    async myRoles (): Promise<IUser> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(['roles'])
        };

        const res = await super.get<IUser>('me', params);
        return res;
    }

    async listAll (fields?: string[]): Promise<IUser[]> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };

        const res = await super.get<IUser[]>('all', params);
        return res;
    }
    async paging (query: BasePaginationQuery, fields?: string[]): Promise<IPagination<IUser>> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };
        if (query.unit) {
            params.queryStringParameters['unit'] = query.unit;
        }
        if (query.notUnit) {
            params.queryStringParameters['notUnit'] = query.notUnit;
        }
        if (query.notRole) {
            params.queryStringParameters['notRole'] = query.notRole;
        }
        if (query.haveManager) {
            params.queryStringParameters['haveManager'] = query.haveManager;
        }
        if (query.hasRole) {
            params.queryStringParameters['hasRole'] = query.hasRole;
        }
        if (query.pillar) {
            params.queryStringParameters['pillar'] = query.pillar;
        }
        if (query.limit) {
            params.queryStringParameters['limit'] = query.limit;
        }
        if (query.offset) {
            params.queryStringParameters['offset'] = query.offset;
        }
        if (query.disabled != null) {
            params.queryStringParameters['disabled'] = query.disabled;
        }
        if (query.search) {
            params.queryStringParameters['search'] = query.search;
        }
        if (query.noAuditor) {
            params.queryStringParameters['noAuditor'] = query.noAuditor;
        }
        if (query.noAudited) {
            params.queryStringParameters['noAudited'] = query.noAudited;
        }
        if (query.noAssignment) {
            params.queryStringParameters['noAssignment'] = query.noAssignment;
        }
        if (query.isManager != null) {
            params.queryStringParameters['isManager'] = query.isManager;
        }

        const res = await super.get<IPagination<IUser>>('', params);
        return res;
    }
    async getById (id: string, fields?: string[]): Promise<IUser> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };
        const res = await super.get<IUser>(id, params);
        return res;
    }
    async create (data: IUser): Promise<IUser> {
        const params: RequestInterface = {
            body: data
        };
        const res = await super.post<IUser>('', params);
        return res;
    }

    async getAccessAWS (role: string): Promise<any> {
        const res = await super.post<IUser>(`access/${role}`);
        return res;
    }
    async updateById (id: string, data: IUser): Promise<IUser> {
        const params: RequestInterface = {
            body: data
        };
        const res = await super.put<IUser>(id, params);
        return res;
    }
    async deleteById (id: string, query: BasePaginationQuery): Promise<void> {
        const params: RequestInterface = {
            queryStringParameters: []
        };
        if (query.force) {
            params.queryStringParameters['force'] = query.force;
        }
        await super.delete<void>(id, params);
    }
    async enableById (id: string): Promise<void> {
        await super.patch<void>(`${id}/enable`);
    }
    async disableById (id: string): Promise<void> {
        await super.patch<void>(`${id}/disable`);
    }

    async unitAdd (id: string, unitId: string): Promise<void> {
        await super.patch<void>(`${id}/unit/${unitId}`);
    }
    async roleAdd (id: string, data: string): Promise<IUser> {
        const params: RequestInterface = {
            body: data
        };
        const res = await super.put<IUser>(`role/user/${id}`, params);
        return res;
    }
    async unitRemove (id: string, unitId: string): Promise<void> {
        await super.delete<void>(`${id}/unit/${unitId}`);
    }
    async roleRemove (id: string, roleId: string): Promise<void> {
        await super.delete<void>(`${id}/role/${roleId}`);
    }
}

